import React, { useState, useEffect, useCallback } from 'react';
import { axios, useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const TL = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(v || 0);

const paymentMethodLabel = (method) => ({
  nakit: 'Nakit',
  kredi_karti: 'Kredi Kartı',
  banka_havalesi: 'Banka Havalesi',
  pos: 'POS'
}[method] || method || '-');

const getLocalDate = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const makeReceiptNo = (taksit) => {
  const today = getLocalDate().replaceAll('-', '');
  const taksitId = String(taksit?.id || taksit?.taksit_id || '0').padStart(5, '0');
  return `MKB-${today}-${taksitId}`;
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const getParentName = (taksit) => {
  const fullName = taksit?.veli_adi || taksit?.veli_ad_soyad || taksit?.veli;
  if (fullName) return fullName;
  return [taksit?.veli_ad, taksit?.veli_soyad].filter(Boolean).join(' ');
};

const getPaidDate = (taksit, currentTaksit, form) => {
  if (taksit.id === currentTaksit.id) return form.odeme_tarihi;
  return taksit.gercek_odeme || taksit.odeme_tarihi || taksit.tahsilat_tarihi || taksit.odendi_tarihi;
};

const formatDate = (date) => date ? new Date(date).toLocaleDateString('tr-TR') : '-';

const getCurrentMonth = () => getLocalDate().slice(0, 7);

const getInstallmentsUrl = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value !== null && value !== undefined) searchParams.set(key, value);
  });
  const query = searchParams.toString();
  return `/muhasebe/taksitler${query ? `?${query}` : ''}`;
};

const getMonthLabel = (month) => month
  ? new Date(`${month}-01T00:00:00`).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })
  : 'Tüm dönemler';

const getPaidAmount = (taksit) => {
  if (taksit.durum !== 'odendi') return 0;
  const recordedAmount = Number(taksit.odeme_tutari || 0);
  return recordedAmount > 0 ? recordedAmount : Number(taksit.tutar || 0);
};

const summarizeInstallments = (installments) => installments.reduce((summary, taksit) => {
  if (taksit.durum === 'iptal') return summary;
  const amount = Number(taksit.tutar || 0);
  summary.expected += amount;
  summary.count += 1;
  if (taksit.durum === 'odendi') summary.paid += getPaidAmount(taksit);
  if (taksit.durum === 'bekliyor' || taksit.durum === 'gecikti') summary.remaining += amount;
  if (taksit.durum === 'gecikti') summary.overdue += amount;
  return summary;
}, { expected: 0, paid: 0, remaining: 0, overdue: 0, count: 0 });

const STATUS_LABELS = {
  odendi: 'Ödendi',
  bekliyor: 'Bekliyor',
  gecikti: 'Gecikmiş',
  iptal: 'İptal',
};

const printCollectionReport = ({ installments, month, className }) => {
  const summary = summarizeInstallments(installments);
  const periodLabel = getMonthLabel(month);
  const reportRows = installments.length
    ? installments.map(t => `
        <tr>
          <td>${escapeHtml(`${t.ad || ''} ${t.soyad || ''}`.trim())}</td>
          <td>${escapeHtml(t.sinif_adi || '-')}</td>
          <td>${escapeHtml(t.paket || '-')}</td>
          <td>${formatDate(t.vade_tarihi)}</td>
          <td>${t.durum === 'odendi' ? formatDate(t.gercek_odeme) : '-'}</td>
          <td class="right">${TL(t.tutar)}</td>
          <td><span class="status ${escapeHtml(t.durum)}">${escapeHtml(STATUS_LABELS[t.durum] || t.durum)}</span></td>
        </tr>`).join('')
    : '<tr><td colspan="7" class="empty">Seçilen filtrelerde tahsilat kaydı bulunamadı.</td></tr>';

  const win = window.open('', '_blank', 'width=1100,height=900');
  if (!win) {
    toast.error('Rapor penceresi açılamadı. Tarayıcı popup iznini kontrol edin.');
    return;
  }

  win.document.write(`<!doctype html>
    <html lang="tr">
      <head>
        <meta charset="utf-8" />
        <title>Tahsilat Raporu - ${escapeHtml(periodLabel)}</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 28px; color: #172033; font-family: Arial, sans-serif; background: #f8fafc; }
          .report { max-width: 1050px; margin: 0 auto; background: #fff; border: 1px solid #dbe2ea; padding: 28px; }
          .head { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #172033; padding-bottom: 16px; }
          .brand { color: #2453c6; font-size: 18px; font-weight: 800; }
          h1 { margin: 5px 0 0; font-size: 25px; }
          .meta { text-align: right; font-size: 13px; line-height: 1.7; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 20px 0; }
          .summary-card { border: 1px solid #dbe2ea; border-radius: 8px; padding: 12px; }
          .summary-card .label { color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
          .summary-card .value { margin-top: 5px; font-size: 18px; font-weight: 800; }
          .expected .value { color: #2563eb; } .paid .value { color: #059669; }
          .remaining .value { color: #d97706; } .overdue .value { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #dbe2ea; padding: 9px 8px; text-align: left; }
          th { background: #eef2ff; font-size: 11px; text-transform: uppercase; }
          .right { text-align: right; white-space: nowrap; }
          .status { font-weight: 700; } .odendi { color: #059669; } .bekliyor { color: #2563eb; }
          .gecikti { color: #dc2626; } .iptal { color: #64748b; }
          .empty { padding: 28px; text-align: center; color: #64748b; }
          .foot { margin-top: 14px; color: #64748b; font-size: 11px; }
          .actions { max-width: 1050px; margin: 14px auto 0; display: flex; justify-content: flex-end; }
          button { border: 0; border-radius: 8px; padding: 11px 18px; background: #2453c6; color: #fff; font-weight: 800; cursor: pointer; }
          @page { size: landscape; margin: 12mm; }
          @media print {
            body { padding: 0; background: #fff; }
            .report { max-width: none; border: 0; padding: 0; }
            .actions { display: none; }
          }
        </style>
      </head>
      <body>
        <main class="report">
          <div class="head">
            <div><div class="brand">ASDER Sistem</div><h1>Tahsilat Raporu</h1></div>
            <div class="meta">
              <div><strong>Dönem:</strong> ${escapeHtml(periodLabel)}</div>
              <div><strong>Sınıf:</strong> ${escapeHtml(className || 'Tüm sınıflar')}</div>
              <div><strong>Rapor tarihi:</strong> ${formatDate(getLocalDate())}</div>
            </div>
          </div>
          <section class="summary">
            <div class="summary-card expected"><div class="label">Beklenen</div><div class="value">${TL(summary.expected)}</div></div>
            <div class="summary-card paid"><div class="label">Alınan</div><div class="value">${TL(summary.paid)}</div></div>
            <div class="summary-card remaining"><div class="label">Kalan</div><div class="value">${TL(summary.remaining)}</div></div>
            <div class="summary-card overdue"><div class="label">Gecikmiş</div><div class="value">${TL(summary.overdue)}</div></div>
          </section>
          <table>
            <thead><tr><th>Öğrenci</th><th>Sınıf</th><th>Paket</th><th>Vade</th><th>Ödeme Tarihi</th><th class="right">Tutar</th><th>Durum</th></tr></thead>
            <tbody>${reportRows}</tbody>
          </table>
          <div class="foot">Toplam ${summary.count} aktif taksit kaydı listelenmiştir.</div>
        </main>
        <div class="actions"><button onclick="window.print()">Yazdır / PDF Kaydet</button></div>
      </body>
    </html>`);
  win.document.close();
  setTimeout(() => win.print(), 500);
};

const printReceipt = ({ taksit, form, cashierName, installments }) => {
  const paidInstallments = installments.filter(t => t.durum === 'odendi' || t.id === taksit.id);
  const dueInstallments = installments.filter(t => t.durum !== 'odendi' && t.id !== taksit.id);
  const paidTotal = paidInstallments.reduce((sum, t) => sum + Number(t.tutar || 0), 0);
  const dueTotal = dueInstallments.reduce((sum, t) => sum + Number(t.tutar || 0), 0);
  const rows = (items, emptyText, showPaidDate = false) => items.length
    ? items.map(t => `
        <tr>
          <td>#${escapeHtml(t.taksit_no || '-')}</td>
          <td>${formatDate(t.vade_tarihi)}</td>
          ${showPaidDate ? `<td>${formatDate(getPaidDate(t, taksit, form))}</td>` : ''}
          <td class="right">${TL(t.tutar)}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="${showPaidDate ? 4 : 3}" class="muted">${emptyText}</td></tr>`;
  const win = window.open('', '_blank', 'width=820,height=900');
  if (!win) {
    toast.error('Makbuz penceresi açılamadı. Tarayıcı popup iznini kontrol edin.');
    return;
  }
  win.document.write(`<!doctype html>
    <html lang="tr">
      <head>
        <meta charset="utf-8" />
        <title>Makbuz ${escapeHtml(form.makbuz_no)}</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 28px; color: #111827; font-family: Arial, sans-serif; background: #f8fafc; }
          .receipt { max-width: 760px; margin: 0 auto; background: #fff; border: 1px solid #d1d5db; padding: 28px; }
          .top { display: flex; justify-content: space-between; gap: 18px; border-bottom: 2px solid #111827; padding-bottom: 16px; }
          h1 { margin: 0; font-size: 24px; letter-spacing: 0; }
          .brand { font-weight: 800; font-size: 18px; margin-bottom: 6px; }
          .meta { text-align: right; line-height: 1.6; font-size: 13px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin: 20px 0; }
          .box { border: 1px solid #e5e7eb; padding: 12px; min-height: 76px; }
          .label { color: #6b7280; font-size: 11px; text-transform: uppercase; font-weight: 700; margin-bottom: 5px; }
          .value { font-size: 15px; font-weight: 700; }
          .amount { background: #ecfdf5; border-color: #10b981; }
          .amount .value { color: #047857; font-size: 22px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
          th, td { border: 1px solid #e5e7eb; padding: 9px 10px; text-align: left; }
          th { background: #f3f4f6; }
          .right { text-align: right; }
          .muted { color: #6b7280; }
          .section-title { margin-top: 20px; font-weight: 800; font-size: 15px; }
          .totals { display: flex; justify-content: flex-end; gap: 20px; margin-top: 10px; font-weight: 800; }
          .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; margin-top: 48px; }
          .signature { border-top: 1px solid #111827; padding-top: 8px; text-align: center; font-size: 13px; }
          .actions { max-width: 760px; margin: 14px auto 0; display: flex; justify-content: flex-end; }
          button { border: 0; background: #ef334e; color: white; border-radius: 8px; padding: 10px 18px; font-weight: 800; cursor: pointer; }
          @media print {
            body { background: #fff; padding: 0; }
            .receipt { border: 0; max-width: none; min-height: 100vh; }
            .actions { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="top">
            <div>
              <div class="brand">ASDER Sistem</div>
              <h1>Tahsilat Makbuzu</h1>
            </div>
            <div class="meta">
              <div><strong>Makbuz No:</strong> ${escapeHtml(form.makbuz_no)}</div>
              <div><strong>Tarih:</strong> ${formatDate(form.odeme_tarihi)}</div>
              <div><strong>Ödeme:</strong> ${escapeHtml(paymentMethodLabel(form.odeme_yontemi))}</div>
            </div>
          </div>

          <div class="grid">
            <div class="box">
              <div class="label">Öğrenci</div>
              <div class="value">${escapeHtml(`${taksit.ad || ''} ${taksit.soyad || ''}`)}</div>
            </div>
            <div class="box">
              <div class="label">Parayı Ödeyen Veli</div>
              <div class="value">${escapeHtml(form.veli_ad_soyad || '-')}</div>
            </div>
            <div class="box">
              <div class="label">Parayı Tahsil Eden</div>
              <div class="value">${escapeHtml(cashierName || form.tahsil_eden_ad_soyad || '-')}</div>
            </div>
            <div class="box amount">
              <div class="label">Tahsil Edilen Tutar</div>
              <div class="value">${TL(form.tutar)}</div>
            </div>
          </div>

          <div class="section-title">Ödenmiş Taksitler</div>
          <table>
            <thead><tr><th>Taksit</th><th>Vade</th><th>Ödendiği Tarih</th><th class="right">Tutar</th></tr></thead>
            <tbody>${rows(paidInstallments, 'Ödenmiş taksit bulunmuyor.', true)}</tbody>
          </table>
          <div class="totals"><span>Ödenmiş Toplam:</span><span>${TL(paidTotal)}</span></div>

          <div class="section-title">Ödenecek Taksitler</div>
          <table>
            <thead><tr><th>Taksit</th><th>Vade</th><th class="right">Tutar</th></tr></thead>
            <tbody>${rows(dueInstallments, 'Ödenecek taksit bulunmuyor.')}</tbody>
          </table>
          <div class="totals"><span>Kalan Toplam:</span><span>${TL(dueTotal)}</span></div>

          <div class="signatures">
            <div class="signature">Ödeyen Veli İmza</div>
            <div class="signature">Tahsil Eden İmza</div>
          </div>
        </div>
        <div class="actions"><button onclick="window.print()">Makbuzu Yazdır</button></div>
      </body>
    </html>`);
  win.document.close();
  setTimeout(() => win.print(), 500);
};

function TahsilatModal({ taksit, taksitler, onSave, onClose, cashierName }) {
  const [form, setForm] = useState({
    tutar: taksit?.tutar || '',
    odeme_yontemi: 'nakit',
    odeme_tarihi: getLocalDate(),
    makbuz_no: makeReceiptNo(taksit),
    veli_ad_soyad: getParentName(taksit),
    tahsil_eden_ad_soyad: cashierName || '',
    notlar: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.tutar) return toast.error('Tutar giriniz');
    if (!form.veli_ad_soyad.trim()) return toast.error('Parayı ödeyen veli adını giriniz');
    if (!form.tahsil_eden_ad_soyad.trim()) return toast.error('Tahsil eden kişi adını giriniz');
    setSaving(true);
    try {
      const backendPayload = {
        taksit_id: taksit.id,
        tutar: form.tutar,
        odeme_yontemi: form.odeme_yontemi,
        odeme_tarihi: form.odeme_tarihi,
        makbuz_no: form.makbuz_no,
        notlar: [
          form.notlar,
          `Parayı ödeyen veli: ${form.veli_ad_soyad}`,
          `Parayı tahsil eden: ${form.tahsil_eden_ad_soyad}`
        ].filter(Boolean).join('\n')
      };
      await onSave(backendPayload);
      const sameStudentInstallments = taksitler.filter(t => {
        if (t.ogrenci_id && taksit.ogrenci_id) return t.ogrenci_id === taksit.ogrenci_id;
        return `${t.ad || ''} ${t.soyad || ''}` === `${taksit.ad || ''} ${taksit.soyad || ''}`;
      });
      printReceipt({
        taksit,
        form,
        cashierName: form.tahsil_eden_ad_soyad,
        installments: sameStudentInstallments.length ? sameStudentInstallments : [taksit]
      });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div className="modal animate-fadeInUp"
        style={{
          maxWidth: 440,
          width: '100%',
          borderRadius: '20px 20px 0 0', // Mobilde alttan açılan panel hissi
          margin: 0,
          paddingBottom: 'env(safe-area-inset-bottom, 20px)'
        }}
        onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ padding: '16px 20px' }}>
          <div className="modal-title">💳 Tahsilat Al</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{taksit.ad} {taksit.soyad}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Taksit #{taksit.taksit_no} · Vade: {new Date(taksit.vade_tarihi).toLocaleDateString('tr-TR')}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: 'var(--blue)', marginTop: 4 }}>{TL(taksit.tutar)}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Tutar *</label>
              <input className="form-input" type="number" value={form.tutar} onChange={e => setForm(f => ({ ...f, tutar: e.target.value }))} style={{ fontSize: '16px' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Ödeme Yöntemi</label>
              <select className="form-input" value={form.odeme_yontemi} onChange={e => setForm(f => ({ ...f, odeme_yontemi: e.target.value }))} style={{ fontSize: '16px' }}>
                <option value="nakit">Nakit</option>
                <option value="kredi_karti">Kredi Kartı</option>
                <option value="banka_havalesi">Banka Havalesi</option>
                <option value="pos">POS</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Tarih</label>
                <input className="form-input" type="date" value={form.odeme_tarihi} onChange={e => setForm(f => ({ ...f, odeme_tarihi: e.target.value }))} style={{ fontSize: '16px' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Makbuz No</label>
                <input className="form-input" value={form.makbuz_no} readOnly style={{ fontSize: '16px', background: 'var(--bg-elevated)' }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Parayı Ödeyen Veli Adı Soyadı *</label>
              <input className="form-input" value={form.veli_ad_soyad} onChange={e => setForm(f => ({ ...f, veli_ad_soyad: e.target.value }))} style={{ fontSize: '16px' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Parayı Tahsil Eden Kişi *</label>
              <input className="form-input" value={form.tahsil_eden_ad_soyad} onChange={e => setForm(f => ({ ...f, tahsil_eden_ad_soyad: e.target.value }))} style={{ fontSize: '16px' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 24 }}>
            <button className="btn btn-secondary" onClick={onClose} style={{ height: 48 }}>İptal</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving} style={{ height: 48, fontSize: '1rem' }}>
              {saving ? <div className="spinner" style={{ width: 18, height: 18 }} /> : 'Makbuz Çıkart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TahsilatPage() {
  const { user } = useAuth();
  const canSendWhatsAppReminder = ['kurucu', 'muhasebe'].includes(user?.rol);
  const [taksitler, setTaksitler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [makbuzTaksitleri, setMakbuzTaksitleri] = useState([]);
  const [filtre, setFiltre] = useState({
    durum: '',
    arama: '',
    sinif_id: '',
    ay: getCurrentMonth(),
  });
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    axios.get('/siniflar')
      .then(r => {
        if (r.data.success) setSiniflar(r.data.data || []);
      })
      .catch(() => toast.error('Sınıflar yüklenemedi'));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(getInstallmentsUrl({
        ay: filtre.ay,
        sinif_id: filtre.sinif_id,
      }));
      if (r.data.success) setTaksitler(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Tahsilat kayıtları yüklenemedi');
    } finally { setLoading(false); }
  }, [filtre.ay, filtre.sinif_id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    try {
      await axios.post('/muhasebe/tahsilat', form);
      toast.success('Tahsilat kaydedildi!');
      setModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Hata');
      throw e;
    }
  };

  const handleWhatsAppReminder = async (taksit) => {
    try {
      const { data } = await axios.post(`/muhasebe/taksitler/${taksit.id}/whatsapp`);
      if (!data?.success) {
        toast.error(data?.message || 'WhatsApp ödeme hatırlatması gönderilemedi.');
        return;
      }
      toast.success(data.message || 'WhatsApp ödeme hatırlatması gönderildi.');
    } catch (error) {
      const message = error.response?.data?.message || 'WhatsApp ödeme hatırlatması gönderilemedi.';
      const templateIssue = /şablon|sablon|template/i.test(message);
      toast.error(templateIssue
        ? `${message} Meta tarafında ödeme hatırlatma şablonunun tanımlı ve aktif olduğunu kontrol edin.`
        : message);
    }
  };

  const openTahsilatModal = async (taksit) => {
    setModal(taksit);
    setMakbuzTaksitleri([taksit]);
    try {
      const r = await axios.get(getInstallmentsUrl({ ogrenci_id: taksit.ogrenci_id }));
      if (r.data.success && r.data.data?.length) setMakbuzTaksitleri(r.data.data);
    } catch {
      // Tahsilat işlemi çalışmaya devam eder; makbuz en azından seçili taksiti içerir.
    }
  };

  const filtrelenmis = taksitler.filter(t => {
    if (filtre.durum && t.durum !== filtre.durum) return false;
    if (!filtre.arama) return true;
    const searchTarget = `${t.ad || ''} ${t.soyad || ''} ${t.sinif_adi || ''} ${t.paket || ''}`;
    return searchTarget.toLocaleLowerCase('tr-TR').includes(filtre.arama.toLocaleLowerCase('tr-TR'));
  });

  const ozet = summarizeInstallments(taksitler);
  const seciliSinif = siniflar.find(s => String(s.id) === String(filtre.sinif_id));
  const donemEtiketi = getMonthLabel(filtre.ay);

  const handlePrintReport = () => printCollectionReport({
    installments: taksitler,
    month: filtre.ay,
    className: seciliSinif?.ad || 'Tüm sınıflar',
  });

  const DURUM_RENK = { odendi: '#10B981', bekliyor: '#3B82F6', gecikti: '#EF4444', iptal: '#94A3B8' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 20, paddingBottom: 40, width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <div className="animate-fadeInUp" style={{ padding: isMobile ? '0 4px' : 0 }}>
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: isMobile ? '1.2rem' : '1.4rem', margin: 0 }}>💳 Tahsilat</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 4 }}>
          {donemEtiketi} için beklenen ve gerçekleşen tahsilatları yönetin
        </p>
      </div>

      {/* Özet Kartları */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 8 : 14
      }} className="animate-fadeInUp stagger-1">
        {[
          { l: 'Beklenen', v: TL(ozet.expected), sub: `${ozet.count} taksit`, c: '#2563EB', bg: '#DBEAFE' },
          { l: 'Alınan', v: TL(ozet.paid), sub: `${taksitler.filter(t => t.durum === 'odendi').length} ödendi`, c: '#059669', bg: '#D1FAE5' },
          { l: 'Kalan', v: TL(ozet.remaining), sub: `${taksitler.filter(t => t.durum === 'bekliyor' || t.durum === 'gecikti').length} bekliyor`, c: '#D97706', bg: '#FEF3C7' },
          { l: 'Gecikmiş', v: TL(ozet.overdue), sub: `${taksitler.filter(t => t.durum === 'gecikti').length} taksit`, c: '#EF4444', bg: '#FEE2E2' }
        ].map((s, i) => (
          <div key={i} className="card" style={{ background: s.bg, border: `1px solid ${s.c}20`, padding: isMobile ? '12px 10px' : 16 }}>
            <div style={{ fontSize: isMobile ? '0.7rem' : '0.75rem', fontWeight: 700, color: s.c, textTransform: 'uppercase', marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: isMobile ? '1rem' : '1.25rem', color: s.c }}>{s.v}</div>
            <div style={{ fontSize: '0.7rem', color: s.c, fontWeight: 600, marginTop: 3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtreler ve Arama */}
      <div className="card animate-fadeInUp stagger-1" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
          <div style={{ display: 'flex', overflowX: 'auto', gap: 6, paddingBottom: isMobile ? 4 : 0, WebkitOverflowScrolling: 'touch' }}>
            {[
              { v: '', l: 'Tümü' },
              { v: 'gecikti', l: 'Gecikmiş' },
              { v: 'bekliyor', l: 'Bekleyen' },
              { v: 'odendi', l: 'Ödendi' }
            ].map(f => (
              <button
                key={f.v}
                className={`chip ${filtre.durum === f.v ? 'active' : ''}`}
                onClick={() => setFiltre(x => ({ ...x, durum: f.v }))}
                style={{ whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '0.8rem' }}
              >
                {f.l}
              </button>
            ))}
          </div>
          <button
            className="btn btn-primary"
            onClick={handlePrintReport}
            disabled={loading || taksitler.length === 0}
            style={{ marginLeft: isMobile ? 0 : 'auto', whiteSpace: 'nowrap', height: 38 }}
          >
            🖨️ Raporu Yazdır / PDF
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(180px, 0.8fr) minmax(200px, 1fr) minmax(220px, 1fr) auto', gap: 10 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Ay</label>
            <input
              className="form-input"
              type="month"
              value={filtre.ay}
              onChange={e => setFiltre(x => ({ ...x, ay: e.target.value }))}
              style={{ width: '100%', height: 40, fontSize: '16px' }}
            />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Sınıf</label>
            <select
              className="form-input"
              value={filtre.sinif_id}
              onChange={e => setFiltre(x => ({ ...x, sinif_id: e.target.value }))}
              style={{ width: '100%', height: 40, fontSize: '16px' }}
            >
              <option value="">Tüm sınıflar</option>
              {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Öğrenci ara</label>
            <input className="form-input" placeholder="🔍 İsim, sınıf veya paket..." value={filtre.arama}
            onChange={e => setFiltre(x => ({ ...x, arama: e.target.value }))}
              style={{ width: '100%', height: 40, fontSize: '16px' }} />
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setFiltre(x => ({ ...x, ay: '' }))}
            style={{ alignSelf: 'end', height: 40, whiteSpace: 'nowrap' }}
          >
            Tüm dönemler
          </button>
        </div>
      </div>

      {/* Liste / Tablo Alanı */}
      <div className="animate-fadeInUp stagger-2">
        {loading ? (
          <div className="page-loader" style={{ minHeight: 200 }}><div className="spinner" /></div>
        ) : filtrelenmis.length === 0 ? (
          <div className="card empty-state" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem' }}>💳</div>
            <h3 style={{ marginTop: 10, fontSize: '1rem' }}>Taksit bulunamadı</h3>
          </div>
        ) : isMobile ? (
          /* MOBİL KART GÖRÜNÜMÜ (Tablo yerine) */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtrelenmis.map((t, i) => (
              <div key={i} className="card" style={{ padding: 14, borderLeft: `5px solid ${DURUM_RENK[t.durum] || '#94A3B8'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.ad} {t.soyad}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {t.sinif_adi || 'Sınıfsız'} · {t.paket}
                    </div>
                    <span
                      className="badge"
                      style={{ display: 'inline-flex', marginTop: 7, background: `${DURUM_RENK[t.durum] || '#94A3B8'}22`, color: DURUM_RENK[t.durum] || '#64748B' }}
                    >
                      {STATUS_LABELS[t.durum] || t.durum}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 900, color: DURUM_RENK[t.durum] }}>{TL(t.tutar)}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>#{t.taksit_no} Taksit</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div>
                      <span style={{ color: 'var(--text-muted)' }}>Vade: </span>
                      <span style={{ fontWeight: 600 }}>{formatDate(t.vade_tarihi)}</span>
                      {t.durum === 'gecikti' && <span style={{ color: '#EF4444', fontWeight: 800, marginLeft: 8 }}>{t.gecikme_gun}G</span>}
                    </div>
                    <div style={{ color: t.durum === 'odendi' ? '#059669' : 'var(--text-muted)', marginTop: 3 }}>
                      Ödeme: {t.durum === 'odendi' ? formatDate(t.gercek_odeme) : '-'}
                    </div>
                  </div>
                  {(t.durum === 'bekliyor' || t.durum === 'gecikti') && (
                    <button className="btn btn-sm" style={{ background: '#10B981', color: 'white', border: 'none', padding: '6px 16px', borderRadius: 8, fontWeight: 700 }}
                      onClick={() => openTahsilatModal(t)}>💳 Tahsilat Al</button>
                  )}
                  {canSendWhatsAppReminder && (t.durum === 'bekliyor' || t.durum === 'gecikti') && (
                    <button className="btn btn-sm" style={{ background: '#15803D', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 8, fontWeight: 700 }}
                      onClick={() => handleWhatsAppReminder(t)}>WhatsApp ile ödeme hatırlatması gönder</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* MASAÜSTÜ TABLO GÖRÜNÜMÜ */
          <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Öğrenci</th><th>Sınıf</th><th>Paket</th><th>Taksit #</th><th>Vade</th><th>Ödeme Tarihi</th><th>Gecikme</th><th style={{ textAlign: 'right' }}>Tutar</th><th>Durum</th><th>İşlem</th></tr></thead>
              <tbody>
                {filtrelenmis.map((t, i) => (
                  <tr key={i} style={{ borderLeft: `4px solid ${DURUM_RENK[t.durum] || '#94A3B8'}` }}>
                    <td style={{ fontWeight: 700 }}>{t.ad} {t.soyad}</td>
                    <td style={{ fontSize: '0.78rem' }}>{t.sinif_adi || 'Sınıfsız'}</td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{t.paket}</td>
                    <td style={{ textAlign: 'center' }}>{t.taksit_no}</td>
                    <td style={{ fontSize: '0.82rem' }}>{formatDate(t.vade_tarihi)}</td>
                    <td style={{ fontSize: '0.82rem', color: t.durum === 'odendi' ? '#059669' : 'var(--text-muted)' }}>
                      {t.durum === 'odendi' ? formatDate(t.gercek_odeme) : '-'}
                    </td>
                    <td>{t.durum === 'gecikti' && <span className="badge" style={{ background: '#FEE2E2', color: '#EF4444' }}>{t.gecikme_gun} gün</span>}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-ui)', fontWeight: 900, color: DURUM_RENK[t.durum] }}>{TL(t.tutar)}</td>
                    <td><span className="badge" style={{ background: (DURUM_RENK[t.durum]) + '22', color: DURUM_RENK[t.durum] }}>{STATUS_LABELS[t.durum] || t.durum}</span></td>
                    <td>
                      {(t.durum === 'bekliyor' || t.durum === 'gecikti') && (
                        <button className="btn btn-sm" style={{ background: '#10B981', color: 'white' }} onClick={() => openTahsilatModal(t)}>💳 Al</button>
                      )}
                      {canSendWhatsAppReminder && (t.durum === 'bekliyor' || t.durum === 'gecikti') && (
                        <button className="btn btn-sm" style={{ background: '#15803D', color: 'white', marginLeft: 6 }}
                          onClick={() => handleWhatsAppReminder(t)}>WhatsApp hatırlat</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <TahsilatModal
          taksit={modal}
          taksitler={makbuzTaksitleri}
          onSave={handleSave}
          onClose={() => setModal(null)}
          cashierName={[user?.ad, user?.soyad].filter(Boolean).join(' ')}
        />
      )}
    </div>
  );
}
